import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import BlogWidget from "@/components/BlogWidget";
import SEO, { pageSEO } from "@/components/SEO";
import LazyImage from "@/components/LazyImage";
import ThemeToggle from "@/components/ThemeToggle";
import { 
  Music, 
  BarChart3, 
  DollarSign, 
  Zap, 
  Globe, 
  Shield, 
  ArrowRight,
  ChevronRight,
  Star,
  CheckCircle2,
  Menu,
  X,
  TrendingUp,
  Users,
  Award,
  BadgeCheck,
  Mic2
} from "lucide-react";

const FEATURES = [
  {
    icon: Globe,
    title: "Global Distribution",
    description: "Release your music to 150+ streaming platforms worldwide including Spotify, Apple Music, and TikTok."
  },
  {
    icon: DollarSign,
    title: "Up to 100% Royalties",
    description: "Keep 90-100% of what you earn. Pro & Select plans keep every penny—no hidden fees."
  },
  {
    icon: BarChart3,
    title: "Real-Time Analytics",
    description: "Track your streams, revenue, and audience insights with our powerful dashboard."
  },
  {
    icon: Zap,
    title: "Fast Distribution",
    description: "Get your music live on major platforms within 24-48 hours guaranteed."
  },
  {
    icon: Shield,
    title: "Rights Protection",
    description: "We protect your music with Content ID and copyright management tools."
  },
  {
    icon: Music,
    title: "Marketing Tools",
    description: "Create smart links, pre-saves, and promo campaigns to grow your fanbase."
  }
];

const PLATFORMS = [
  { name: "Spotify", color: "#1DB954" },
  { name: "Apple Music", color: "#FA243C" },
  { name: "YouTube Music", color: "#FF0000" },
  { name: "Amazon Music", color: "#FF9900" },
  { name: "TikTok", color: "#010101" },
  { name: "Deezer", color: "#FF0092" }
];

// Trust credentials and partnerships
const CREDENTIALS = [
  {
    title: "A2IM Member",
    description: "American Association of Independent Music",
    icon: "badge"
  },
  {
    title: "BIMA Fellow",
    description: "Black Independent Music Accelerator",
    icon: "award"
  }
];

const ARTIST_CREDITS = [
  "Grammy Award Recipients",
  "Grammy Nominees", 
  "Billboard #1 Artists",
  "RIAA Platinum & Gold Artists"
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <SEO {...pageSEO.home} />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <LazyImage 
                src="https://customer-assets.emergentagent.com/job_royalty-revolution/artifacts/gc921mlg_IMG_1214.png" 
                alt="SYCE.me" 
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/distribution" className="text-muted-foreground hover:text-foreground transition-colors">Distribution</Link>
              <Link to="/podcast-hosting" className="text-muted-foreground hover:text-foreground transition-colors">Podcasts</Link>
              <Link to="/audio-services" className="text-muted-foreground hover:text-foreground transition-colors">Audio Services</Link>
              <Link to="/sync" className="text-muted-foreground hover:text-foreground transition-colors">Sync</Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-pricing">Pricing</Link>
              <ThemeToggle />
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="text-foreground">Log In</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-bold">
                  Get Started
                </Button>
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-card border-t border-border"
          >
            <nav className="flex flex-col p-6 gap-4">
              <Link to="/distribution" className="text-lg" onClick={() => setMobileMenuOpen(false)}>Distribution</Link>
              <Link to="/podcast-hosting" className="text-lg" onClick={() => setMobileMenuOpen(false)}>Podcasts</Link>
              <Link to="/audio-services" className="text-lg" onClick={() => setMobileMenuOpen(false)}>Audio Services</Link>
              <Link to="/sync" className="text-lg" onClick={() => setMobileMenuOpen(false)}>Sync</Link>
              <Link to="/pricing" className="text-lg" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <a href="#features" className="text-lg" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Theme:</span>
                <ThemeToggle />
              </div>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full">Log In</Button>
              </Link>
              <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-primary text-primary-foreground">Get Started</Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1648260029310-5f1da359af9d?w=1920&q=80)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
          <div className="hero-glow absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm text-primary mb-6">
                <Zap className="w-4 h-4" />
                Game-changing royalty distribution
              </span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black leading-tight mb-6 tracking-tight"
              data-testid="hero-title"
              aria-label="Music Distribution Platform - Your Music, Your Revenue, Your Rules"
            >
              YOUR MUSIC.<br />
              <span className="text-primary">YOUR REVENUE.</span><br />
              YOUR RULES.
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-muted-foreground max-w-2xl mb-10"
            >
              <strong>Distribute your music</strong> to Spotify, Apple Music, TikTok & 150+ platforms. 
              Keep <strong>up to 100% of your royalties</strong> and take control of your career with industry-leading analytics.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button 
                size="lg" 
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold btn-press"
                onClick={() => navigate("/register")}
                data-testid="hero-cta"
              >
                Start Distributing
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-8 sm:gap-16 mt-16"
            >
              <div>
                <div className="font-mono text-3xl sm:text-4xl font-bold text-primary">150+</div>
                <div className="text-muted-foreground">Platforms</div>
              </div>
              <div>
                <div className="font-mono text-2xl sm:text-3xl font-bold text-primary">Up to 100%</div>
                <div className="text-muted-foreground">Royalties</div>
              </div>
              <div>
                <div className="font-mono text-3xl sm:text-4xl font-bold text-primary">24hr</div>
                <div className="text-muted-foreground">Distribution</div>
              </div>
              <div>
                <div className="font-mono text-3xl sm:text-4xl font-bold text-primary">DMV</div>
                <div className="text-muted-foreground">Based</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platforms Marquee */}
      <section className="py-12 border-y border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-muted-foreground mb-8">Distribute to all major platforms</p>
          <div className="flex flex-wrap justify-center gap-8 sm:gap-16">
            {PLATFORMS.map((platform) => (
              <div key={platform.name} className="flex items-center gap-2 text-lg font-medium opacity-70 hover:opacity-100 transition-opacity">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: platform.color }} />
                {platform.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="features-title">
              Everything You Need to<br /><span className="text-primary">Succeed</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Built by artists, for artists. We understand what you need to take your music career to the next level.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="feature-card bg-card border border-border p-8 hover:border-primary/30 transition-all card-hover"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Revenue Section - Artist Empowerment */}
      <section className="py-24 lg:py-32 bg-card/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Keep <span className="text-primary revenue-highlight">Up to 100%</span> of Your Royalties
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Unlike other distributors that take 15-30% of your earnings, SYCE lets you keep 90-100% depending on your plan. 
                Pro & Select plans keep every dollar—we make money through subscriptions, not by taking your royalties.
              </p>
              <ul className="space-y-4">
                {[
                  "90% royalties on Starter & Essential plans",
                  "100% royalties on Pro & Select plans",
                  "Weekly payouts with no minimum (paid plans)",
                  "Transparent earnings breakdown"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-card border border-border p-8 relative z-10">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm text-primary mb-4">
                    <Zap className="w-4 h-4" />
                    Why Artists Choose SYCE
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4 p-4 bg-accent/50 rounded-lg">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Music className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Unlimited Releases</h4>
                      <p className="text-sm text-muted-foreground">Release as much music as you want for one flat annual fee</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-accent/50 rounded-lg">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Worldwide Reach</h4>
                      <p className="text-sm text-muted-foreground">Get your music on Spotify, Apple Music, TikTok & 150+ more</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-accent/50 rounded-lg">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Live Analytics Dashboard</h4>
                      <p className="text-sm text-muted-foreground">Track streams, revenue, and audience insights daily</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-accent/50 rounded-lg">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-bold mb-1">Black-Owned & Artist First</h4>
                      <p className="text-sm text-muted-foreground">Built by artists who understand your journey</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-primary/30 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Credentials Section */}
      <section id="credentials" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="credentials-title">
              Trusted by <span className="text-primary">Industry Professionals</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              We've provided quality audio services to award-winning artists and partnered with premier venues in the DMV area.
            </p>
          </div>

          {/* Membership Badges */}
          <div className="flex flex-wrap justify-center gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 bg-card border border-border rounded-lg px-6 py-4"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                <BadgeCheck className="w-7 h-7 text-primary" />
              </div>
              <div>
                <div className="font-heading font-bold text-lg">A2IM Member</div>
                <div className="text-sm text-muted-foreground">American Association of Independent Music</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-4 bg-card border border-border rounded-lg px-6 py-4"
            >
              <div className="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center">
                <Award className="w-7 h-7 text-secondary" />
              </div>
              <div>
                <div className="font-heading font-bold text-lg">BIMA Fellow</div>
                <div className="text-sm text-muted-foreground">Black Independent Music Accelerator</div>
              </div>
            </motion.div>
          </div>

          {/* Artist Credits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-card via-card/80 to-card border border-border rounded-xl p-8 mb-16"
          >
            <div className="text-center mb-8">
              <h3 className="font-heading text-xl font-bold mb-2">We've Worked With</h3>
              <p className="text-muted-foreground text-sm">Professional audio services for award-winning talent</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {ARTIST_CREDITS.map((credit, idx) => (
                <motion.div
                  key={credit}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2 bg-background/50 border border-border rounded-full px-5 py-2.5"
                >
                  <Star className="w-4 h-4 text-primary fill-primary" />
                  <span className="font-medium text-sm">{credit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Bottom Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 pt-12 border-t border-border"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="font-mono text-3xl sm:text-4xl font-bold text-primary">100%</div>
                <div className="text-muted-foreground text-sm mt-1">Black Owned</div>
              </div>
              <div>
                <div className="font-mono text-3xl sm:text-4xl font-bold text-primary">DMV</div>
                <div className="text-muted-foreground text-sm mt-1">Based</div>
              </div>
              <div>
                <div className="font-mono text-3xl sm:text-4xl font-bold text-primary">150+</div>
                <div className="text-muted-foreground text-sm mt-1">Platforms</div>
              </div>
              <div>
                <div className="font-mono text-3xl sm:text-4xl font-bold text-primary">100%</div>
                <div className="text-muted-foreground text-sm mt-1">Your Royalties</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Podcast & Distribution Promo Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Podcast Hosting Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cyan/10 to-cyan/5 border border-cyan/30 rounded-2xl p-8 hover:border-cyan/50 transition-all group"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan/20 border border-cyan/30 mb-6">
                <Mic2 className="w-4 h-4 text-cyan" />
                <span className="text-xs text-cyan font-medium">NEW</span>
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
                Podcast <span className="text-cyan">Hosting</span>
              </h3>
              <p className="text-muted-foreground mb-6">
                Professional podcast hosting with one-click distribution to Apple Podcasts, Spotify, 
                and 10+ platforms. Start your free 30-day trial today.
              </p>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan flex-shrink-0" />
                  <span>RSS 2.0 compliant feeds</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan flex-shrink-0" />
                  <span>10+ podcast directories</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan flex-shrink-0" />
                  <span>Advanced analytics</span>
                </li>
              </ul>
              <Button 
                className="bg-cyan text-black hover:bg-cyan/90 rounded-full px-6 font-bold group-hover:scale-105 transition-transform"
                onClick={() => navigate("/podcast-hosting")}
              >
                Learn More
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>

            {/* Music & Video Distribution Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/30 rounded-2xl p-8 hover:border-primary/50 transition-all group"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/20 border border-primary/30 mb-6">
                <Music className="w-4 h-4 text-primary" />
                <span className="text-xs text-primary font-medium">CORE</span>
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
                Music & Video <span className="text-primary">Distribution</span>
              </h3>
              <p className="text-muted-foreground mb-6">
                Get your music and videos on Spotify, Apple Music, YouTube, TikTok and 150+ platforms worldwide. 
                Keep up to 100% of your royalties. Start free or upgrade for more features.
              </p>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>150+ streaming platforms</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>Music videos to YouTube, Vevo & more</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>YouTube Content ID protection</span>
                </li>
              </ul>
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-bold group-hover:scale-105 transition-transform"
                onClick={() => navigate("/distribution")}
              >
                Learn More
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Videos Section */}
      <section className="py-24 lg:py-32 bg-card/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="text-secondary">Featured Videos</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Music videos from artists distributed through SYCE
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Video 1 - Tidal iframe embed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-secondary/50 transition-all"
              data-testid="featured-video-1"
            >
              <div className="aspect-[9/16] sm:aspect-video">
                <iframe 
                  src="https://embed.tidal.com/videos/204314227?layout=gridify&allowFullScreen=true"
                  frameBorder="0"
                  allow="encrypted-media"
                  allowFullScreen
                  className="w-full h-full"
                  title="SYCE Featured Video 1"
                ></iframe>
              </div>
            </motion.div>

            {/* Video 2 - Tidal iframe embed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-secondary/50 transition-all"
              data-testid="featured-video-2"
            >
              <div className="aspect-[9/16] sm:aspect-video">
                <iframe 
                  src="https://embed.tidal.com/videos/201297888?layout=gridify&allowFullScreen=true"
                  frameBorder="0"
                  allow="encrypted-media"
                  allowFullScreen
                  className="w-full h-full"
                  title="SYCE Featured Video 2"
                ></iframe>
              </div>
            </motion.div>

            {/* Video 3 - Tidal iframe embed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-secondary/50 transition-all"
              data-testid="featured-video-3"
            >
              <div className="aspect-[9/16] sm:aspect-video">
                <iframe 
                  src="https://embed.tidal.com/videos/361473279?layout=gridify&allowFullScreen=true"
                  frameBorder="0"
                  allow="encrypted-media"
                  allowFullScreen
                  className="w-full h-full"
                  title="SYCE Featured Video 3"
                ></iframe>
              </div>
            </motion.div>

            {/* Video 4 - Tidal iframe embed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-secondary/50 transition-all"
              data-testid="featured-video-4"
            >
              <div className="aspect-[9/16] sm:aspect-video">
                <iframe 
                  src="https://embed.tidal.com/videos/208341816?layout=gridify&allowFullScreen=true"
                  frameBorder="0"
                  allow="encrypted-media"
                  allowFullScreen
                  className="w-full h-full"
                  title="SYCE Featured Video 4"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blog Widget */}
      <BlogWidget limit={3} showHeader={true} />

      {/* FAQ Section - SEO Content */}
      <section className="py-24 lg:py-32 border-t border-border">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Frequently Asked <span className="text-primary">Questions</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Everything you need to know about music distribution with SYCE
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-lg font-bold mb-3">What is music distribution?</h3>
              <p className="text-muted-foreground">
                Music distribution is the process of getting your music onto streaming platforms like Spotify, Apple Music, TikTok, YouTube Music, Amazon Music, and over 150 other platforms worldwide. SYCE handles all the technical requirements, metadata submission, and platform relationships so you can focus on creating music. Our <Link to="/distribution" className="text-primary hover:underline">music distribution service</Link> ensures your tracks reach billions of potential listeners globally.
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-lg font-bold mb-3">How much does SYCE cost?</h3>
              <p className="text-muted-foreground">
                SYCE offers flexible <Link to="/pricing" className="text-primary hover:underline">pricing plans</Link> starting with a free Starter tier (1 free upload, 90% royalties). Our Essential plan is $29.99/year for 5 singles + 1 album per year with 90% royalties. Pro ($79/year) and Select ($149/year) plans offer unlimited releases with 100% royalties, plus premium features like Content ID protection and dedicated support. All plans include mastering for the first year!
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-lg font-bold mb-3">Do I really keep 100% of my royalties?</h3>
              <p className="text-muted-foreground">
                On our Pro and Select plans—yes! You keep 100% of your streaming royalties. Our Starter and Essential plans have a 10% royalty share (you keep 90%), which helps us offer affordable pricing for emerging artists. Unlike other distributors that take 15-30%, SYCE offers some of the best rates in the industry. Your music, your money—that's our promise.
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-lg font-bold mb-3">How long does distribution take?</h3>
              <p className="text-muted-foreground">
                Most releases go live within 24-48 hours on major platforms like Spotify and Apple Music. Some platforms may take up to 5-7 days for initial releases. Our Select plan members get same-day priority processing. We recommend submitting releases at least 2 weeks before your target release date to allow time for playlist pitching and promotion.
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-lg font-bold mb-3">Does SYCE offer podcast hosting?</h3>
              <p className="text-muted-foreground">
                Yes! Our <Link to="/podcast-hosting" className="text-primary hover:underline">podcast hosting service</Link> includes automatic distribution to Apple Podcasts, Spotify, Google Podcasts, Amazon Music, and 10+ podcast directories. We provide RSS 2.0 compliant feeds, advanced analytics, and premium features like private podcasting. Start with a free 30-day trial—no credit card required.
              </p>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-lg font-bold mb-3">What is sync licensing?</h3>
              <p className="text-muted-foreground">
                Sync licensing is when your music is licensed for use in TV shows, films, commercials, video games, and other media. Our <Link to="/sync" className="text-primary hover:underline">sync licensing marketplace</Link> connects you directly with verified music supervisors, ad agencies, and production companies. Browse real opportunities and submit your tracks for consideration—placements can pay anywhere from $500 to $50,000+.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Partners Section */}
      <section className="py-16 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Trusted Partners</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We partner with industry leaders to provide you with the best services for your music career
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-6 items-center">
            {/* Easy Song - Music Licensing */}
            <a 
              href="https://www.easysong.com/referral.aspx?PartnerID=424" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 shadow-sm group-hover:shadow-md transition-shadow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_c919ce80-bd55-455f-b831-fe2f07022cbf/artifacts/9w2g3lpn_EasySong.png" 
                  alt="Easy Song Licensing" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Easy Song</p>
                <p className="text-[10px] text-muted-foreground">Music Licensing</p>
              </div>
            </a>

            {/* Soundcharts - Analytics */}
            <a 
              href="https://www.soundcharts.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-2xl font-bold text-blue-600">SC</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Soundcharts</p>
                <p className="text-[10px] text-muted-foreground">Music Analytics</p>
              </div>
            </a>

            {/* Chartmetric - Analytics */}
            <a 
              href="https://www.chartmetric.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-lg font-bold">CM</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Chartmetric</p>
                <p className="text-[10px] text-muted-foreground">Music Data</p>
              </div>
            </a>

            {/* SubmitHub - Playlist Pitching */}
            <a 
              href="https://www.submithub.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-xl font-bold">SH</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">SubmitHub</p>
                <p className="text-[10px] text-muted-foreground">Playlist Pitching</p>
              </div>
            </a>

            {/* Audiam - Royalty Collection */}
            <a 
              href="https://www.audiam.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-black rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-lg font-bold">Audiam</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Audiam</p>
                <p className="text-[10px] text-muted-foreground">Royalty Collection</p>
              </div>
            </a>

            {/* Berklee Online - Education */}
            <a 
              href="https://online.berklee.edu" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-red-700 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-xs font-bold text-center leading-tight">Berklee<br/>Online</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Berklee Online</p>
                <p className="text-[10px] text-muted-foreground">Music Education</p>
              </div>
            </a>

            {/* Recording Academy - Industry */}
            <a 
              href="https://www.grammy.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-black text-xl">🏆</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Recording Academy</p>
                <p className="text-[10px] text-muted-foreground">Grammy Awards</p>
              </div>
            </a>

            {/* Disc Makers - Physical Distribution */}
            <a 
              href="https://www.discmakers.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-blue-800 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-xs font-bold text-center">Disc<br/>Makers</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Disc Makers</p>
                <p className="text-[10px] text-muted-foreground">CD/Vinyl Pressing</p>
              </div>
            </a>

            {/* Show.co - Concert Marketing */}
            <a 
              href="https://www.show.co" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-lg font-bold">Show</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Show.co</p>
                <p className="text-[10px] text-muted-foreground">Concert Marketing</p>
              </div>
            </a>

            {/* Guitar Center - Music Gear */}
            <a 
              href="https://www.guitarcenter.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-[10px] font-bold text-center leading-tight">Guitar<br/>Center</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Guitar Center</p>
                <p className="text-[10px] text-muted-foreground">Music Gear</p>
              </div>
            </a>

            {/* Sweetwater - Music Equipment */}
            <a 
              href="https://www.sweetwater.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-[10px] font-bold text-center">Sweetwater</span>
              </div>
              <div className="text-center">
                <p className="font-semibold text-xs">Sweetwater</p>
                <p className="text-[10px] text-muted-foreground">Music Equipment</p>
              </div>
            </a>
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Need to license a cover song or clear a sample?{" "}
              <a 
                href="https://www.easysong.com/referral.aspx?PartnerID=424" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Get licensed with Easy Song →
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Take Control?
          </h2>
          <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            Join SYCE today and start earning what you deserve. Start free or upgrade for more features.
          </p>
          <Button 
            size="lg"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-bold btn-press"
            onClick={() => navigate("/register")}
            data-testid="cta-button"
          >
            Get Started Now
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-8 mb-12">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <LazyImage 
                  src="https://customer-assets.emergentagent.com/job_royalty-revolution/artifacts/gc921mlg_IMG_1214.png" 
                  alt="SYCE.me" 
                  className="h-8 w-auto"
                />
              </Link>
              <p className="text-muted-foreground text-sm">
                Empowering artists to own their music and maximize their revenue.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/distribution" className="hover:text-foreground transition-colors">Music Distribution</Link></li>
                <li><Link to="/podcast-hosting" className="hover:text-foreground transition-colors">Podcast Hosting</Link></li>
                <li><Link to="/sync" className="hover:text-foreground transition-colors">Sync Licensing</Link></li>
                <li><Link to="/audio-services" className="hover:text-foreground transition-colors">Mixing & Mastering</Link></li>
                <li><Link to="/pricing" className="hover:text-foreground transition-colors">Pricing Plans</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/blog" className="hover:text-foreground transition-colors">Artist Blog</Link></li>
                <li><Link to="/register" className="hover:text-foreground transition-colors">Create Account</Link></li>
                <li><Link to="/login" className="hover:text-foreground transition-colors">Sign In</Link></li>
                <li><Link to="/contact" className="hover:text-foreground transition-colors">Contact Support</Link></li>
                <li><Link to="/partners/login" className="hover:text-foreground transition-colors">Partner Portal</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Partners</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="https://www.easysong.com/referral.aspx?PartnerID=424" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Easy Song Licensing</a></li>
                <li><a href="https://www.soundcharts.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Soundcharts Analytics</a></li>
                <li><a href="https://www.chartmetric.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Chartmetric</a></li>
                <li><a href="https://www.submithub.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">SubmitHub</a></li>
                <li><a href="https://www.audiam.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Audiam Royalties</a></li>
                <li><a href="https://online.berklee.edu" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Berklee Online</a></li>
                <li><a href="https://www.grammy.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Recording Academy</a></li>
                <li><a href="https://www.discmakers.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Disc Makers</a></li>
                <li><a href="https://www.show.co" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Show.co</a></li>
                <li><a href="https://www.guitarcenter.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Guitar Center</a></li>
                <li><a href="https://www.sweetwater.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Sweetwater</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
                <li><Link to="/distribution-agreement" className="hover:text-foreground transition-colors">Distribution Agreement</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">© 2025 SYCE. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="https://x.com/SYCE_Ent" target="_blank" rel="noopener" className="text-muted-foreground hover:text-foreground transition-colors">X</a>
              <a href="https://www.instagram.com/syce.me/" target="_blank" rel="noopener" className="text-muted-foreground hover:text-foreground transition-colors">Instagram</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
